
import { supabase } from '@/lib/supabase';
import { handleSupabaseError, validateDetectionType, validateDetectionData } from './utils';
import { logInfo, logError } from '@/utils/logger';
import { 
  selectServicePartition, 
  canMakeServiceRequest, 
  trackServiceResult,
  SHARD_TYPES, 
  generateShardKey 
} from '@/utils/scalingStrategies';

// Define a consistent return type interface 
export interface SaveResultResponse {
  status: number;
  error?: string;
  data: any;
}

// Save detection result with scaling and sharding support
export const saveDetectionResult = async (userId: string, analysisType: string, result: any): Promise<SaveResultResponse> => {
  try {
    // Validate detection type
    if (!validateDetectionType(analysisType)) {
      return {
        status: 400,
        error: "Invalid analysis type",
        data: null
      };
    }
    
    // Create data object
    const detectionData = {
      user_id: userId,
      analysis_type: analysisType,
      score: result.score,
      confidence: result.confidence,
      flags: result.flags || [],
      explanation: result.explanation,
      model_name: result.modelName,
      metadata: {
        timestamp: result.timestamp || new Date(),
        details: result.details || {},
        partition_info: {} // Will be populated with partition information
      }
    };
    
    // Validate data
    const validationResult = validateDetectionData(detectionData);
    if (!validationResult.valid) {
      return {
        status: 400,
        error: validationResult.errors,
        data: null
      };
    }
    
    // Check service availability and rate limits
    if (!canMakeServiceRequest(userId, analysisType, 'saveDetectionResult')) {
      return {
        status: 429,
        error: "Service temporarily unavailable or rate limit exceeded. Please try again later.",
        data: null
      };
    }
    
    // Select appropriate service partition
    const partition = selectServicePartition(analysisType, userId);
    if (!partition) {
      return {
        status: 503,
        error: "No service partition available for this analysis type",
        data: null
      };
    }
    
    // Add partition information to metadata for debugging and analysis
    detectionData.metadata.partition_info = {
      partition_id: partition.id,
      region: partition.region,
      timestamp: new Date().toISOString()
    };
    
    // Generate shard key (in a real implementation, this would determine which database to write to)
    const shardKey = generateShardKey(SHARD_TYPES.USER, userId);
    
    // Log operation for tracing
    logInfo('Saving detection result', 'save-result', {
      analysis_type: analysisType,
      partition: partition.id,
      region: partition.region,
      shard_key: shardKey
    });
    
    // Perform the database operation
    const { data, error } = await supabase
      .from('detection_results')
      .insert(detectionData)
      .select()
      .single();
    
    if (error) {
      // Record service failure
      trackServiceResult(analysisType, false);
      return handleSupabaseError(error, "Failed to save detection result");
    }
    
    // Record service success
    trackServiceResult(analysisType, true);
    
    return {
      status: 200,
      data
    };
  } catch (error) {
    // Record service failure
    trackServiceResult(analysisType, false);
    logError('Save detection result', error);
    
    return {
      status: 500,
      error: "An unexpected error occurred",
      data: null
    };
  }
};
